import { memo, useEffect, useRef, useState } from 'react';
import { Text, Tooltip, useDisclosure } from '@chakra-ui/react';

import { useWindowResize, useDebounce } from 'shared/hooks';

/**
 * Компонент `TooltipText` отображает текст с возможностью показа тултипа, если текст не умещается в контейнер.
 * Работает как по клику, так и при наведении.
 *
 * param {string} `title` - Текст заголовка, который будет отображаться.
 *
 * param {Object} `titleProps=null` - Дополнительные свойства компонента `Text`.
 *
 * param {Object} `tooltipProps=null` - Дополнительные свойства для компонента `Tooltip`.
 *
 * param {boolean} `isFullWidth=false` - Флаг, указывающий, должен ли тултип быть по ширине равен ширине текста.
 *
 * @example
 * <TooltipText title="Очень длинный заголовок, который не умещается в строке" />
 */
const TooltipText = ({ title, titleProps = null, tooltipProps = null, isFullWidth = false }) => {
  const titleRef = useRef(null);
  const [titleWidth, setTitleWidth] = useState();
  const [isTitleOverflow, setIsTitleOverflow] = useState(false);
  const { isOpen, onToggle, onClose, onOpen } = useDisclosure();
  const { width } = useWindowResize();
  const debouncedValue = useDebounce(width, 500);

  useEffect(() => {
    if (titleRef.current) {
      const { scrollHeight, clientHeight, offsetWidth } = titleRef.current;
      setIsTitleOverflow(scrollHeight > clientHeight);
      isFullWidth && setTitleWidth(offsetWidth);
    }
  }, [title, debouncedValue]);

  return (
    <Tooltip
      hasArrow
      isOpen={isTitleOverflow && isOpen}
      label={title}
      bgColor="bg.black"
      sx={
        isFullWidth && {
          width: '100%',
          maxW: `${titleWidth}px`,
        }
      }
      {...tooltipProps}
    >
      <Text
        as="h3"
        ref={titleRef}
        textStyle={{ mobile: 'header.h4', laptop: 'h4', desktop: 'header.h4' }}
        dangerouslySetInnerHTML={{ __html: title }}
        noOfLines={2}
        onClick={onToggle}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        {...titleProps}
      />
    </Tooltip>
  );
};

export default memo(TooltipText);
